import React, { useState, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import PasswordInput from '../components/common/PasswordInput'
import { motion, AnimatePresence } from 'framer-motion'
import {
  UserCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ShieldExclamationIcon,
  CalendarDaysIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'
import { formContainer, formItem, alertSlideIn, authCard, buttonTap } from '../config/animations'

export default function AccountPage() {
  usePageTitle('My Account')
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [showDangerZone, setShowDangerZone] = useState(false)
  const errorRef = useRef(null)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    password_confirmation: ''
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError('')
    if (success) setSuccess('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    const userData = {
      name: formData.name,
      email: formData.email
    }

    if (formData.password) {
      userData.password = formData.password
      userData.password_confirmation = formData.password_confirmation
    }

    try {
      const response = await fetch('/users', {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({ user: userData }),
        credentials: 'include'
      })

      if (response.ok) {
        setSuccess('Your account has been updated successfully.')
        setFormData(prev => ({
          ...prev,
          password: '',
          password_confirmation: ''
        }))
        setShowPasswordSection(false)
      } else {
        const data = await response.json()
        if (data.errors) {
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => `${field.charAt(0).toUpperCase() + field.slice(1)} ${messages.join(', ')}`)
            .join('. ')
          setError(errorMessages)
        } else {
          setError(data.message || 'Failed to update account')
        }
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      setError('An error occurred while updating your account. Please try again.')
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } finally {
      setIsLoading(false)
    }
  }

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null

  return (
    <div className="auth-page !items-start !pt-8 sm:!pt-12">
      <motion.div
        {...authCard}
        className="w-full max-w-lg relative z-10"
      >
        {/* Header with avatar */}
        <motion.div
          variants={formItem}
          initial="initial"
          animate="animate"
          className="text-center mb-8"
        >
          {/* Gradient avatar */}
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5
              bg-gradient-to-br from-primary-400 to-primary-600
              dark:from-primary-500 dark:to-primary-700
              shadow-md"
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          >
            <span className="text-3xl font-bold text-white select-none">
              {userInitial}
            </span>
          </motion.div>

          <h1 className="text-2xl sm:text-3xl font-bold text-text">
            Account Settings
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            Manage your profile and preferences
          </p>

          {/* Member info strip */}
          <div className="mt-4 flex items-center justify-center gap-4 text-sm text-text-muted">
            {user?.email && (
              <span className="inline-flex items-center gap-1.5">
                <EnvelopeIcon className="w-4 h-4" />
                {user.email}
              </span>
            )}
            {memberSince && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDaysIcon className="w-4 h-4" />
                Member since {memberSince}
              </span>
            )}
          </div>
        </motion.div>

        {/* Alerts */}
        <div ref={errorRef}>
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                {...alertSlideIn}
                className="alert-danger"
                role="alert"
              >
                <ExclamationTriangleIcon className="alert-icon text-red-500 dark:text-red-500" />
                <p>{error}</p>
              </motion.div>
            )}
            {success && (
              <motion.div
                key="success"
                {...alertSlideIn}
                className="alert-success"
                role="status"
              >
                <CheckCircleIcon className="alert-icon text-green-600 dark:text-green-500" />
                <p>{success}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Profile card */}
        <div className="auth-card">
          <motion.form
            variants={formContainer}
            initial="initial"
            animate="animate"
            onSubmit={handleSubmit}
          >
            {/* Profile section header */}
            <motion.div variants={formItem} className="flex items-center gap-2.5 mb-6">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg
                bg-primary-50 dark:bg-primary-50">
                <UserCircleIcon className="w-4.5 h-4.5 text-primary-600 dark:text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-text">
                Profile Information
              </h2>
            </motion.div>

            {/* Name field */}
            <motion.div variants={formItem} className="input-group mb-5">
              <label htmlFor="account-name" className="input-label">
                Full name
              </label>
              <input
                id="account-name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
                className="input-field-enhanced"
              />
            </motion.div>

            {/* Email field */}
            <motion.div variants={formItem} className="input-group mb-6">
              <label htmlFor="account-email" className="input-label">
                Email address
              </label>
              <input
                id="account-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                className="input-field-enhanced"
              />
            </motion.div>

            {/* Password section divider */}
            <motion.div variants={formItem} className="border-t border-border pt-6 mb-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg
                    bg-surface-100 dark:bg-surface-100">
                    <LockClosedIcon className="w-4.5 h-4.5 text-text-secondary" />
                  </div>
                  <h2 className="text-lg font-semibold text-text">
                    Change Password
                  </h2>
                </div>
                <motion.button
                  type="button"
                  whileTap={buttonTap}
                  onClick={() => {
                    setShowPasswordSection(!showPasswordSection)
                    if (showPasswordSection) {
                      setFormData(prev => ({ ...prev, password: '', password_confirmation: '' }))
                    }
                  }}
                  className="text-sm font-semibold px-3 py-1.5 rounded-md
                    text-primary-600 dark:text-primary-600
                    hover:bg-primary-50 dark:hover:bg-primary-50
                    transition-colors"
                >
                  {showPasswordSection ? 'Cancel' : 'Update'}
                </motion.button>
              </div>
              {!showPasswordSection && (
                <p className="mt-2 ml-[42px] text-sm text-text-muted">
                  Click "Update" to change your password
                </p>
              )}
            </motion.div>

            {/* Password fields (collapsible) */}
            <AnimatePresence>
              {showPasswordSection && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="space-y-5 pb-6 pl-0">
                    <div className="input-group">
                      <label htmlFor="account-password" className="input-label">
                        New password
                      </label>
                      <PasswordInput
                        id="account-password"
                        name="password"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="At least 6 characters"
                        disabled={isLoading}
                        className="input-field-enhanced"
                      />
                      <p className="input-help">
                        Leave blank to keep your current password
                      </p>
                    </div>

                    <div className="input-group">
                      <label htmlFor="account-password-confirm" className="input-label">
                        Confirm new password
                      </label>
                      <PasswordInput
                        id="account-password-confirm"
                        name="password_confirmation"
                        autoComplete="new-password"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        placeholder="Re-enter new password"
                        disabled={isLoading}
                        className="input-field-enhanced"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit button */}
            <motion.div variants={formItem}>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileTap={!isLoading ? buttonTap : undefined}
                whileHover={!isLoading ? { scale: 1.01 } : undefined}
                className="btn-primary w-full min-h-[52px] text-base font-semibold
                  shadow-md hover:shadow-lg transition-shadow"
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving changes...
                  </span>
                ) : (
                  'Save changes'
                )}
              </motion.button>
            </motion.div>
          </motion.form>
        </div>

        {/* Danger Zone */}
        <motion.div
          variants={formItem}
          initial="initial"
          animate="animate"
          className="mt-6"
        >
          <button
            type="button"
            onClick={() => setShowDangerZone(!showDangerZone)}
            className="w-full flex items-center justify-between px-5 py-3
              rounded-xl border border-border
              text-sm font-medium text-text-muted
              hover:text-red-500 dark:hover:text-red-500
              hover:border-red-200 dark:hover:border-red-200
              transition-colors"
          >
            <span className="inline-flex items-center gap-2">
              <ShieldExclamationIcon className="w-4.5 h-4.5" />
              Danger Zone
            </span>
            <motion.span
              animate={{ rotate: showDangerZone ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="text-xs"
            >
              ▼
            </motion.span>
          </button>

          <AnimatePresence>
            {showDangerZone && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-3 p-5 rounded-xl border border-red-200 dark:border-red-200
                  bg-red-50/50 dark:bg-red-50/30">
                  <h3 className="text-sm font-semibold text-red-700 dark:text-red-500 mb-2">
                    Delete Account
                  </h3>
                  <p className="text-sm text-red-600/80 dark:text-red-500/70 mb-4">
                    Once you delete your account, there is no going back. All your sales and data will be permanently removed.
                  </p>
                  <motion.button
                    type="button"
                    whileTap={buttonTap}
                    className="btn-sm bg-red-500 text-white hover:bg-red-600
                      focus-visible:outline-red-500 shadow-sm
                      font-medium"
                  >
                    Delete my account
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </div>
  )
}
